/* eslint-disable no-console */
import { Sentry } from '~/clients/sentry'
import { TAgeGroup, TGender } from '~/entities'

import { dorisLog } from '../dorisLog'
import { IVerifyV2Response } from '../verify/types'
import { EOrigin, ISettings, TNormalizedSettings } from './types'
import { DorisStartEventError } from './utils'

/**
 * Custom event class for handling Doris widget initialization where the settings/params are validated and normalized.
 * Extends CustomEvent with specific Doris start event settings.
 * @extends {CustomEvent<TNormalizedSettings<EOrigin>>}
 */
class DorisStartEvent<T extends EOrigin> extends CustomEvent<TNormalizedSettings<T>> {
  constructor(origin: EOrigin, settings?: ISettings) {
    const normalizedSettings = DorisStartEvent.normalizedSettingsBuilder(origin, settings)
    super('doris-start', { detail: normalizedSettings as TNormalizedSettings<T> })

    this.checkDorisWidgetNodes()
  }

  /**
   * @param {EOrigin} origin - The origin of the event
   * @param {ISettings} settings - The settings to initialize the event with
   * @returns {DorisStartEvent|null} A new DorisStartEvent instance or null if creation fails
   */
  static create<T extends EOrigin>(origin: T, settings?: ISettings): DorisStartEvent<T> | null {
    dorisLog(
      `Starting MIX with ${
        settings?.skus &&
        (typeof settings.skus === 'string' || (Array.isArray(settings.skus) && settings.skus.length > 0))
          ? `skus: ` + (typeof settings.skus === 'string' ? settings.skus : settings.skus.join(', '))
          : 'no skus'
      }`,
    )

    try {
      const dorisStartEvent = new DorisStartEvent<T>(origin, settings)
      dorisLog('MIX started successfully')

      return dorisStartEvent
    } catch (error) {
      console.error(`[DORIS][INIT] ${error.message}`)
      Sentry.captureException({
        errorName: error instanceof DorisStartEventError ? error.name : 'UNKNOWN_INITIALIZATION_ERROR',
        errorMessage: error.message,
        filePath: 'src/utils/dorisStartEvent/index.ts',
        functionName: origin,
        payload: settings,
      })

      return null
    }
  }

  private checkDorisWidgetNodes() {
    const dorisWidgetContainers = document.querySelectorAll('#doris-widget')

    if (!dorisWidgetContainers.length) {
      dorisLog('Container not found. Widget will not be injected.')
      Sentry.captureException({
        errorName: 'WIDGET_ELEMENT_NOT_FOUND',
        errorMessage: 'The element for the widget was not found',
        filePath: 'src/utils/dorisStartEvent/index.ts',
        functionName: 'start',
      })
    }

    dorisWidgetContainers.forEach((element, index) => {
      if (index) {
        console.warn('[DORIS][INIT] Warning! There must not be more than one div in the DOM with id="doris-widget"')

        if (element.childNodes.length) {
          console.warn('[DORIS][INIT] The element ', element, ' should not render the element ', element.childNodes[0])
        }
      }
    })
  }

  private static normalizedSettingsBuilder(
    origin: EOrigin,
    settings?: ISettings,
  ): TNormalizedSettings<EOrigin> | undefined {
    const {
      normalizedStartSettingsBuilder,
      normalizedInjectButtonSettingsBuilder,
      normalizedFetchSkuWithUrlValidatedSettings,
    } = DorisStartEvent

    if (origin === EOrigin.Start) {
      return normalizedStartSettingsBuilder(settings)
    }

    if (origin === EOrigin.InjectButton) {
      return normalizedInjectButtonSettingsBuilder(settings)
    }

    // origin === EOrigin.FetchSkuWithUrlParameters
    return normalizedFetchSkuWithUrlValidatedSettings(settings)
  }

  private static normalizedStartSettingsBuilder(settings?: ISettings): TNormalizedSettings<EOrigin.Start> {
    const { normalizeSkus, normalizeDefaultGender, normalizeDefaultAgeGroup, normalizeWebview, normalizeAppuser } =
      DorisStartEvent

    return {
      skus: normalizeSkus(settings?.skus),
      defaultGender: normalizeDefaultGender(settings?.defaultGender),
      defaultAgeGroup: normalizeDefaultAgeGroup(settings?.defaultAgeGroup),
      webview: normalizeWebview(settings?.webview),
      appuser: normalizeAppuser(settings?.appuser),
    }
  }

  private static normalizedInjectButtonSettingsBuilder(
    settings?: ISettings,
  ): TNormalizedSettings<EOrigin.InjectButton> {
    const { normalizeSkus, normalizeProductValidated, normalizeVerifyResponse } = DorisStartEvent

    return {
      skus: normalizeSkus(settings?.skus),
      productValidated: normalizeProductValidated(settings?.productValidated),
      verifyResponse: normalizeVerifyResponse(settings?.verifyResponse),
    }
  }

  private static normalizedFetchSkuWithUrlValidatedSettings(
    settings?: ISettings,
  ): TNormalizedSettings<EOrigin.FetchSkuWithUrlParameters> {
    const { normalizeSkus, normalizeDefaultGender, normalizeWebview, normalizeAppuser } = DorisStartEvent

    return {
      skus: normalizeSkus(settings?.skus),
      defaultGender: normalizeDefaultGender(settings?.defaultGender),
      webview: normalizeWebview(settings?.webview),
      appuser: normalizeAppuser(settings?.appuser),
    }
  }

  private static normalizeSkus(skus: ISettings['skus']): string[] {
    if (!skus) return []

    skus = Array.isArray(skus) ? skus : [skus]

    return skus.map(sku => String(sku).trim())
  }

  private static normalizeDefaultGender(defaultGender: ISettings['defaultGender']): TGender | null {
    if (!defaultGender) return null

    const normalizedDefaultGender = defaultGender.trim().toUpperCase()
    if (!['MALE', 'FEMALE'].includes(normalizedDefaultGender)) {
      throw new DorisStartEventError({
        name: 'INVALID_DEFAULT_GENDER',
        message: 'The "defaultGender" parameter is only valid for values "male" or "female"',
      })
    }

    return normalizedDefaultGender as TGender
  }

  private static normalizeDefaultAgeGroup(defaultAgeGroup: ISettings['defaultAgeGroup']): TAgeGroup | null {
    if (!defaultAgeGroup) return null

    const normalizedDefaultAgeGroup = defaultAgeGroup.trim().toUpperCase()
    if (!['ADULTS', 'CHILDREN'].includes(normalizedDefaultAgeGroup)) {
      throw new DorisStartEventError({
        name: 'INVALID_AGE_GROUP',
        message: 'The "defaultAgeGroup" parameter is only valid for values "adults" or "children"',
      })
    }

    return normalizedDefaultAgeGroup as TAgeGroup
  }

  private static normalizeWebview(webview: ISettings['webview'] = null): boolean | null {
    return webview
  }

  private static normalizeAppuser(appuser: ISettings['appuser']): string | null {
    return appuser ? appuser.trim() : null
  }

  private static normalizeProductValidated(productValidated: ISettings['productValidated'] = null): boolean | null {
    return productValidated
  }

  private static normalizeVerifyResponse(verifyResponse: ISettings['verifyResponse'] = null): IVerifyV2Response | null {
    return verifyResponse
  }
}

export default DorisStartEvent
